/**
 * Null-safe wrappers around `localStorage` / `sessionStorage`.
 *
 * Accessing Web Storage can fail in several environments we actually see in production:
 * - Private/Incognito modes and some in-app webviews expose `window.localStorage` as
 *   `null` (or make it throw on access), so `localStorage.getItem(...)` throws
 *   `Cannot read properties of null (reading 'getItem')`.
 * - Storage that is full or disabled throws on `setItem`.
 * - Server-side rendering has no `window` at all.
 *
 * These wrappers degrade gracefully (reads return `null`, writes return `false`) instead
 * of throwing, so callers never crash on an unavailable store. Prefer these over touching
 * `localStorage` / `sessionStorage` directly.
 */

type StorageType = 'localStorage' | 'sessionStorage';

/**
 * Resolve the underlying store, tolerating the cases where merely *accessing*
 * `window[type]` throws or returns `null`.
 */
const resolveStore = (type: StorageType): Storage | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return window[type] ?? null;
  } catch {
    return null;
  }
};

const createSafeStorage = (type: StorageType) => {
  const getItem = (key: string): string | null => {
    const store = resolveStore(type);
    if (!store) {
      return null;
    }
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  };

  const setItem = (key: string, value: string): boolean => {
    const store = resolveStore(type);
    if (!store) {
      return false;
    }
    try {
      store.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  };

  const removeItem = (key: string): boolean => {
    const store = resolveStore(type);
    if (!store) {
      return false;
    }
    try {
      store.removeItem(key);
      return true;
    } catch {
      return false;
    }
  };

  const clear = (): boolean => {
    const store = resolveStore(type);
    if (!store) {
      return false;
    }
    try {
      store.clear();
      return true;
    } catch {
      return false;
    }
  };

  /** Read and JSON-parse a value, returning `fallback` when missing, blocked, or malformed. */
  const getJSON = <T>(key: string, fallback: T | null = null): T | null => {
    const raw = getItem(key);
    if (raw === null) {
      return fallback;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  };

  /** JSON-stringify and write a value. Returns `false` if serialization or the write fails. */
  const setJSON = (key: string, value: unknown): boolean => {
    try {
      return setItem(key, JSON.stringify(value));
    } catch {
      return false;
    }
  };

  return {getItem, setItem, removeItem, clear, getJSON, setJSON};
};

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');
